import { isNumber } from 'lodash';
import moment from 'moment';

import { getErrorReporter } from '../../../utils/errors';
import {
  DataTableCellContent,
  DataTableProps,
  DataTableSortConfig
} from '../types';
import { getPlainText } from '../plainText';

type SortFunctionType = NonNullable<
  NonNullable<DataTableProps<string>['sortableColumns']>[string]
>['type'];

type SortFunction = (
  data: DataTableCellContent<string>[],
  sorting: DataTableSortConfig<string>
) => DataTableCellContent<string>[];

const sortAlphabetic: SortFunction = (data, { key: columnKey, direction }) => {
  if (direction === 'none') {
    return [...data];
  }

  const sortFn = (
    a: DataTableCellContent<string>,
    b: DataTableCellContent<string>
  ) => {
    const aValue = getPlainText(a[columnKey]);
    const bValue = getPlainText(b[columnKey]);

    if (typeof aValue !== 'string' || typeof bValue !== 'string') {
      getErrorReporter().logAndCaptureWarning(
        'Cannot handle non-string values in alphabetic sorting'
      );
      return 0;
    }

    if (direction === 'asc') {
      return aValue.localeCompare(bValue);
    }

    return bValue.localeCompare(aValue);
  };

  return [...data].sort(sortFn);
};

const sortDate: SortFunction = (data, { key: columnKey, direction }) => {
  if (direction === 'none') {
    return [...data];
  }

  const sortFn = (
    a: DataTableCellContent<string>,
    b: DataTableCellContent<string>
  ) => {
    const aValue = getPlainText(a[columnKey]);
    const bValue = getPlainText(b[columnKey]);

    if (typeof aValue !== 'string' || typeof bValue !== 'string') {
      getErrorReporter().logAndCaptureWarning(
        'Cannot handle non-string values in date sorting'
      );
      return 0;
    }

    const aDate = moment(aValue);
    const bDate = moment(bValue);

    if (!aDate.isValid() || !bDate.isValid()) {
      getErrorReporter().logAndCaptureWarning(
        'Cannot handle non-date values in date sorting'
      );
      return 0;
    }

    if (direction === 'asc') {
      return aDate.diff(bDate);
    }

    return bDate.diff(aDate);
  };

  return [...data].sort(sortFn);
};

const sortDollarStrings: SortFunction = (
  data,
  { key: columnKey, direction }
) => {
  if (direction === 'none') {
    return [...data];
  }

  const sortFn = (
    a: DataTableCellContent<string>,
    b: DataTableCellContent<string>
  ) => {
    const aValue = getPlainText(a[columnKey]);
    const bValue = getPlainText(b[columnKey]);

    if (typeof aValue !== 'string' || typeof bValue !== 'string') {
      getErrorReporter().logAndCaptureWarning(
        'Cannot handle non-string values in dollar string sorting'
      );
      return 0;
    }

    const aNumericValue = parseFloat(aValue.slice(1));
    const bNumericValue = parseFloat(bValue.slice(1));

    if (isNaN(aNumericValue) || isNaN(bNumericValue)) {
      getErrorReporter().logAndCaptureWarning(
        'Cannot handle non-numeric values in dollar string sorting'
      );
      return 0;
    }

    if (direction === 'asc') {
      return aNumericValue - bNumericValue;
    }

    return bNumericValue - aNumericValue;
  };

  return [...data].sort(sortFn);
};

const sortNumeric: SortFunction = (data, { key: columnKey, direction }) => {
  if (direction === 'none') {
    return [...data];
  }

  const sortFn = (
    a: DataTableCellContent<string>,
    b: DataTableCellContent<string>
  ) => {
    const aValue = getPlainText(a[columnKey]);
    const bValue = getPlainText(b[columnKey]);

    if (!isNumber(aValue) || !isNumber(bValue)) {
      getErrorReporter().logAndCaptureWarning(
        'Cannot handle non-numeric values in numeric sorting'
      );
      return 0;
    }

    if (direction === 'asc') {
      return aValue - bValue;
    }

    return bValue - aValue;
  };

  return [...data].sort(sortFn);
};

const sortFunctionMapping: Record<SortFunctionType, SortFunction> = {
  alphabetic: sortAlphabetic,
  date: sortDate,
  dollarStrings: sortDollarStrings,
  numeric: sortNumeric
};

const getSortFunction = <T extends string>(
  sortableColumns: DataTableProps<T>['sortableColumns'],
  columnKey: T
) => {
  const columnConfig = sortableColumns?.[columnKey];

  if (!columnConfig) {
    return undefined;
  }

  return sortFunctionMapping[columnConfig.type];
};

export const sortData = <T extends string>(
  data: DataTableCellContent<T>[],
  sortableColumns: DataTableProps<T>['sortableColumns'],
  sorting: DataTableSortConfig<T> | undefined
) => {
  if (!sorting || sorting.direction === 'none') {
    return data;
  }

  const sortFunction = getSortFunction(sortableColumns, sorting.key);

  if (!sortFunction) {
    return data;
  }

  return sortFunction(data, sorting);
};
